import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { ArrowLeft, Save, Send, Star } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PreencherAvaliacao = () => {
  const { evaluationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [avaliacao, setAvaliacao] = useState(null);
  const [competencias, setCompetencias] = useState([]);
  const [respostas, setRespostas] = useState({});
  const [comentarioGeral, setComentarioGeral] = useState('');
  const [prioridadesDesenvolvimento, setPrioridadesDesenvolvimento] = useState('');

  useEffect(() => {
    loadData();
  }, [evaluationId]);

  const loadData = async () => {
    try {
      setLoading(true);
      let hasExistingResponses = false;
      
      // Buscar avaliação se tiver ID
      if (evaluationId && evaluationId !== 'new') {
        try {
          const evalResponse = await axios.get(
            `${API}/api/performance/evaluations/${evaluationId}`,
            {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            }
          );
          
          console.log('📥 Avaliação carregada:', evalResponse.data);
          setAvaliacao(evalResponse.data);
          
          // Se já tem respostas salvas, carregar
          if (evalResponse.data.responses && evalResponse.data.responses.length > 0) {
            console.log('✅ Carregando', evalResponse.data.responses.length, 'respostas salvas');
            const savedRespostas = {};
            evalResponse.data.responses.forEach(resp => {
              savedRespostas[resp.competency_id] = resp;
            });
            setRespostas(savedRespostas);
            hasExistingResponses = true;
          } else {
            console.log('⚠️ Nenhuma resposta encontrada na avaliação');
          }
          
          if (evalResponse.data.overall_comments) {
            setComentarioGeral(evalResponse.data.overall_comments);
          }
          
          if (evalResponse.data.development_priorities) {
            setPrioridadesDesenvolvimento(
              Array.isArray(evalResponse.data.development_priorities)
                ? evalResponse.data.development_priorities.join(', ')
                : evalResponse.data.development_priorities
            );
          }
        } catch (error) {
          console.error('Erro ao carregar avaliação:', error);
        }
      }
      
      // Buscar competências
      const compResponse = await axios.get(`${API}/api/performance/competencies`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setCompetencias(compResponse.data || []);
      
      // Inicializar respostas vazias SOMENTE se NÃO tiver respostas existentes
      if (!hasExistingResponses) {
        console.log('🆕 Inicializando respostas vazias');
        const initialRespostas = {};
        (compResponse.data || []).forEach(comp => {
          initialRespostas[comp.id] = {
            competency_id: comp.id,
            rating: 0,  // 0 = não avaliado ainda
            comments: '',
            evidence: ''
          };
        });
        setRespostas(initialRespostas);
      }
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados da avaliação');
    } finally {
      setLoading(false);
    }
  };

  const handleRatingChange = (competencyId, rating) => {
    setRespostas(prev => ({
      ...prev,
      [competencyId]: {
        ...prev[competencyId],
        rating: parseInt(rating)
      }
    }));
  };

  const handleCommentChange = (competencyId, field, value) => {
    setRespostas(prev => ({
      ...prev,
      [competencyId]: {
        ...prev[competencyId],
        [field]: value
      }
    }));
  };

  const calcularProgresso = () => {
    const total = competencias.length;
    const preenchidas = Object.values(respostas).filter(r => r.rating > 0).length;
    return total > 0 ? (preenchidas / total) * 100 : 0;
  };

  const salvarRascunho = async () => {
    try {
      setSaving(true);
      
      const responsesArray = Object.values(respostas);
      
      await axios.put(
        `${API}/api/performance/evaluations/${evaluationId}`,
        {
          cycle_id: avaliacao?.cycle_id || '',
          employee_id: avaliacao?.employee_id || '',
          evaluator_type: avaliacao?.evaluator_type || 'superior',
          responses: responsesArray,
          overall_comments: comentarioGeral,
          development_priorities: prioridadesDesenvolvimento.split(',').map(p => p.trim()).filter(p => p),
          status: 'draft'
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      toast.success('Rascunho salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar rascunho');
    } finally {
      setSaving(false);
    }
  };

  const finalizarAvaliacao = async () => {
    try {
      // Validar se todas as competências foram avaliadas
      const todasAvaliadas = competencias.every(comp => 
        respostas[comp.id] && respostas[comp.id].rating > 0
      );
      
      if (!todasAvaliadas) {
        toast.error('Por favor, avalie todas as competências antes de finalizar');
        return;
      }
      
      setSaving(true);
      
      const responsesArray = Object.values(respostas);
      
      await axios.put(
        `${API}/api/performance/evaluations/${evaluationId}`,
        {
          cycle_id: avaliacao?.cycle_id || '',
          employee_id: avaliacao?.employee_id || '',
          evaluator_type: avaliacao?.evaluator_type || 'superior',
          responses: responsesArray,
          overall_comments: comentarioGeral,
          development_priorities: prioridadesDesenvolvimento.split(',').map(p => p.trim()).filter(p => p),
          status: 'completed'
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      toast.success('Avaliação finalizada com sucesso!');
      navigate('/minhas-avaliacoes');
    } catch (error) {
      console.error('Erro ao finalizar:', error);
      toast.error('Erro ao finalizar avaliação');
    } finally {
      setSaving(false);
    }
  };

  const getRatingLabel = (rating) => {
    const labels = {
      1: 'Abaixo do Esperado',
      2: 'Parcialmente Atende',
      3: 'Atende',
      4: 'Supera',
      5: 'Excepcional'
    };
    return labels[rating] || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando avaliação...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Preencher Avaliação de Desempenho
              </h1>
              <p className="text-gray-600 mt-1">
                Avalie as competências do colaborador
              </p>
            </div>
            <Badge variant="outline" className="text-lg px-4 py-2">
              {Math.round(calcularProgresso())}% Completo
            </Badge>
          </div>
        </div>

        {/* Barra de Progresso */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Progress value={calcularProgresso()} className="flex-1" />
              <span className="text-sm text-gray-600">
                {Object.values(respostas).filter(r => r.rating > 0).length} de {competencias.length}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Competências */}
        <div className="space-y-6">
          {competencias.map((comp, index) => (
            <Card key={comp.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-xl">
                      {index + 1}. {comp.name}
                    </CardTitle>
                    <CardDescription className="mt-2">
                      {comp.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Escala de Avaliação */}
                <div>
                  <Label className="text-base font-semibold mb-3 block">
                    Avaliação *
                  </Label>
                  {respostas[comp.id]?.rating === 0 && (
                    <div className="mb-2 text-sm text-orange-600 bg-orange-50 p-2 rounded">
                      ⚠️ Esta competência ainda não foi avaliada
                    </div>
                  )}
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map(rating => (
                      <button
                        key={rating}
                        onClick={() => handleRatingChange(comp.id, rating)}
                        className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                          respostas[comp.id]?.rating === rating
                            ? 'border-indigo-600 bg-indigo-50'
                            : respostas[comp.id]?.rating === 0
                            ? 'border-orange-200 hover:border-orange-400'
                            : 'border-gray-200 hover:border-indigo-300'
                        }`}
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="flex">
                            {Array.from({ length: rating }).map((_, i) => (
                              <Star
                                key={i}
                                className={`h-5 w-5 ${
                                  respostas[comp.id]?.rating === rating
                                    ? 'fill-indigo-600 text-indigo-600'
                                    : 'fill-gray-300 text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-xs font-medium text-center">
                            {getRatingLabel(rating)}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Comentários */}
                <div>
                  <Label htmlFor={`comments-${comp.id}`}>
                    Comentários
                  </Label>
                  <Textarea
                    id={`comments-${comp.id}`}
                    placeholder="Adicione observações sobre o desempenho nesta competência..."
                    value={respostas[comp.id]?.comments || ''}
                    onChange={(e) => handleCommentChange(comp.id, 'comments', e.target.value)}
                    rows={3}
                  />
                </div>

                {/* Evidências */}
                <div>
                  <Label htmlFor={`evidence-${comp.id}`}>
                    Evidências / Exemplos
                  </Label>
                  <Textarea
                    id={`evidence-${comp.id}`}
                    placeholder="Descreva situações ou exemplos específicos..."
                    value={respostas[comp.id]?.evidence || ''}
                    onChange={(e) => handleCommentChange(comp.id, 'evidence', e.target.value)}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Comentário Geral e Prioridades */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Comentários Finais</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="comentario-geral">
                Comentário Geral sobre o Desempenho
              </Label>
              <Textarea
                id="comentario-geral"
                placeholder="Resumo geral sobre o desempenho do colaborador..."
                value={comentarioGeral}
                onChange={(e) => setComentarioGeral(e.target.value)}
                rows={4}
              />
            </div>

            <div>
              <Label htmlFor="prioridades">
                Prioridades de Desenvolvimento (separadas por vírgula)
              </Label>
              <Textarea
                id="prioridades"
                placeholder="Ex: Liderança, Comunicação, Gestão de Tempo..."
                value={prioridadesDesenvolvimento}
                onChange={(e) => setPrioridadesDesenvolvimento(e.target.value)}
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Botões de Ação */}
        <div className="flex gap-4 mt-6 pb-6">
          <Button
            variant="outline"
            onClick={salvarRascunho}
            disabled={saving}
            className="flex-1"
          >
            <Save className="mr-2 h-4 w-4" />
            Salvar Rascunho
          </Button>
          
          <Button
            onClick={finalizarAvaliacao}
            disabled={saving || calcularProgresso() < 100}
            className="flex-1 bg-indigo-600 hover:bg-indigo-700"
          >
            <Send className="mr-2 h-4 w-4" />
            Finalizar Avaliação
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PreencherAvaliacao;
